import { Reference } from '@ninetailed/experience.js';
import { ExperienceConfiguration } from '@ninetailed/experience.js-next';
import { useConfig } from 'config';
import { useCommon } from 'contexts/common';
import { INavigationElement } from 'global';
import { useIsExistingCustomer } from 'hooks/common/use-is-existing-customer';
import { INavigationData } from 'interfaces/navigation-data';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { parseExperiences } from 'utils/ninetailed';
import { NAVIGATION_ELEMENT_CONFIGS } from './data';
import { useNavigationExperiment } from './use-navigation-experiment';
import { useNavigationExperiments } from './use-navigation-experiments';
import { useProcessedNavigationElements } from './use-processed-navigation-elements';
import {
  commonTrustbar,
  createNavigationFromContentful,
  footer,
  isNavigation,
} from './utils';
import { createReference } from './utils/empty-reference';
import {
  cleanNavigationForExperiments,
  createExperimentData,
  findNavigationElement,
} from './utils/experiment-helpers';

export interface ElementsByConfig {
  config: (typeof NAVIGATION_ELEMENT_CONFIGS)[number];
  element: INavigationElement | null;
  experimentData: {
    baseline: Reference | null;
    experiences: ExperienceConfiguration<Reference>[];
  };
}

/**
 * This hook returns the navigation data for the current shop based on the type of customer (NC or EC).
 */
const useNavigationData = (): INavigationData => {
  const { isEc: isExistingCustomer } = useIsExistingCustomer();
  const { navigation } = useCommon();
  const { homeDogUrl, homeCatUrl } = useConfig();
  const { formatMessage } = useIntl();

  const localizedMessages = useMemo(
    () => ({
      dogTitle: formatMessage({ id: 'common:dogs' }),
      catTitle: formatMessage({ id: 'common:cats' }),
    }),
    [formatMessage]
  );

  // Clean navigation data for experiments with circular references to avoid errors
  const cleanedNavigation = cleanNavigationForExperiments(navigation);

  const navigationExperiment = useNavigationExperiment({
    baseline: createReference(cleanedNavigation),
    experiences: cleanedNavigation
      ? parseExperiences({ entry: cleanedNavigation })
      : [],
    experimentName: cleanedNavigation?.fields?.title ?? 'main-navigation',
  });

  // Use the experimented navigation if available, otherwise fall back to original
  const navigationElements = useMemo(() => {
    const finalNavigation = navigationExperiment.variant ?? navigation;

    if (isNavigation(finalNavigation)) {
      return finalNavigation.fields.element ?? undefined;
    }

    return undefined;
  }, [navigationExperiment.variant, navigation]);

  // Create a map of elements by their configuration for easy access
  const elementsByConfig = useMemo(() => {
    if (!navigationElements) return new Map<string, ElementsByConfig>();

    const elementsMap = new Map<string, ElementsByConfig>();

    NAVIGATION_ELEMENT_CONFIGS.forEach((config) => {
      const element = findNavigationElement(navigationElements, config);
      const key = `${config.type}_${config.petType}_${config.targetGroup}`;
      elementsMap.set(key, {
        config,
        element,
        experimentData: createExperimentData(element),
      });
    });

    return elementsMap;
  }, [navigationElements]);

  // Use the custom hooks for better separation of concerns
  const experiments = useNavigationExperiments(elementsByConfig);
  const processedElements = useProcessedNavigationElements(
    navigationElements,
    experiments
  );

  // Generate basic fallback navigation to avoid errors
  const fallbackNavigationData = useMemo(
    () => ({
      mobile: { dogs: [], cats: [] },
      desktop: [],
      footer: footer(),
      trustbar: commonTrustbar,
    }),
    []
  );

  // Final navigation data processing
  const navigationData = useMemo(() => {
    if (
      processedElements &&
      Array.isArray(processedElements) &&
      processedElements.length > 0
    ) {
      return createNavigationFromContentful({
        elements: processedElements,
        isExistingCustomer,
        dogTitle: localizedMessages.dogTitle,
        catTitle: localizedMessages.catTitle,
        homeDogUrl,
        homeCatUrl,
      });
    }

    return fallbackNavigationData;
  }, [
    processedElements,
    isExistingCustomer,
    localizedMessages.dogTitle,
    localizedMessages.catTitle,
    homeDogUrl,
    homeCatUrl,
    fallbackNavigationData,
  ]);

  try {
    return navigationData;
  } catch (error) {
    console.error('Navigation data hook error:', error);
    // Eventually we could create a full fallback navigation data with all the elements
    // based on Shop ID and type of customer and store it in the codebase
    return fallbackNavigationData;
  }
};

export default useNavigationData;
